"use client";

import React, { useState, useEffect, useRef } from "react";
import { useGlobalData } from "@/components/TinaContext";
import Link from "next/link";

import Logo from "@/assets/logo.svg";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cn, tinaFieldSafe } from "@/utils";
import { Button } from "./ui/button";

import {
  motion,
  useScroll,
  useMotionValueEvent,
  AnimatePresence,
} from "motion/react";
import { isInternalLink } from "@/components/ui/button";
import { X } from "lucide-react";
import {
  NAVBAR_BG_SCROLL_THRESHOLD,
  NAVBAR_HIDE_SCROLL_THRESHOLD,
} from "@/lib/navbar-scroll";
import { getAlertDismissStorageKey } from "@/lib/alert-dismiss";

export default function Navbar() {
  const navItems = useGlobalData();

  const navMenus = navItems?.navigation?.navMenus;
  const navButtons = navItems?.navigation?.navButtons;
  const alertData = navItems?.alert;

  const { scrollY } = useScroll();

  const [hidden, setHidden] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState("bg-background/70");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);
  const [touchedItem, setTouchedItem] = useState<string | null>(null);
  const [isAlertDismissed, setIsAlertDismissed] = useState(false);
  const scrollPosition = useRef(0);
  const justClosedMenu = useRef(false);
  // Coalesce scroll callbacks to at most one execution per animation frame.
  // Without this, motion's `useMotionValueEvent` fires `update()` on every
  // scroll tick, and each setState invalidates style which the very next
  // scroll tick's value read would then force layout to recompute. This is
  // the ~30 ms forced-reflow source Lighthouse flagged inside the shared
  // 1192-… chunk.
  const rafId = useRef<number | null>(null);
  const prevScrollY = useRef(0);

  const update = (latest: number) => {
    if (justClosedMenu.current) {
      return;
    }

    if (latest < prevScrollY.current) {
      setHidden(false);
    } else if (
      latest > NAVBAR_HIDE_SCROLL_THRESHOLD &&
      latest > prevScrollY.current
    ) {
      setHidden(true);
    }
    if (!isMobileMenuOpen) {
      if (latest > NAVBAR_BG_SCROLL_THRESHOLD) {
        if (backgroundColor !== "bg-background") {
          setBackgroundColor("bg-background");
        }
      } else {
        if (backgroundColor !== "bg-background/70") {
          setBackgroundColor("bg-background/70");
        }
      }
    }

    prevScrollY.current = latest;
  };

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (rafId.current !== null) return;
    rafId.current = window.requestAnimationFrame(() => {
      rafId.current = null;
      update(latest);
    });
  });

  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        window.cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setBackgroundColor("bg-background");
      scrollPosition.current = window.scrollY;
      document.body.classList.add("prevent-scroll");
      document.body.style.top = `-${scrollPosition.current}px`;
    } else {
      // Reset submenu and touch state when closing
      setActiveSubmenu(null);
      setTouchedItem(null);

      const wasOpen = scrollPosition.current > 0;

      if (wasOpen) {
        justClosedMenu.current = true;
        setHidden(false);
        document.body.classList.remove("prevent-scroll");
        document.body.style.top = "";
        window.scrollTo(0, scrollPosition.current);

        // Let the update function handle background color after restoration
        setTimeout(() => {
          justClosedMenu.current = false;
        }, 500);
      }
    }

    return () => {
      document.body.classList.remove("prevent-scroll");
      document.body.style.top = "";
    };
  }, [isMobileMenuOpen]);

  // Reset touched item when submenu changes
  useEffect(() => {
    setTouchedItem(null);
  }, [activeSubmenu]);

  // Check alert dismissal state from local storage
  useEffect(() => {
    if (typeof window !== "undefined" && alertData?.enabled) {
      const storageKey = getAlertDismissStorageKey(
        alertData.message,
        alertData.expiryDate
      );

      if (!storageKey) {
        setIsAlertDismissed(false);
        return;
      }

      const dismissed = localStorage.getItem(storageKey);
      setIsAlertDismissed(dismissed === "true");
    }
  }, [alertData?.expiryDate, alertData?.message, alertData?.enabled]);

  // Check if alert should be shown
  const shouldShowAlert = () => {
    if (!alertData?.enabled || isAlertDismissed) {
      return false;
    }

    // Check if alert has expired
    if (alertData?.expiryDate) {
      const expiryDate = new Date(alertData.expiryDate);
      const now = new Date();
      if (expiryDate < now) {
        return false;
      }
    }

    return true;
  };

  // Handle alert dismissal
  const handleDismissAlert = () => {
    const storageKey = getAlertDismissStorageKey(
      alertData?.message,
      alertData?.expiryDate
    );

    if (!storageKey) return;

    localStorage.setItem(storageKey, "true");
    setIsAlertDismissed(true);
  };

  return (
    <motion.nav
      id="site-navbar"
      className={cn(
        "border-b border-gray-700 sticky top-0 z-50 transition-colors duration-300",
        backgroundColor
      )}
      animate={hidden ? "hidden" : "visible"}
      variants={{ hidden: { y: -180 }, visible: { y: 0 } }}
      transition={{ ease: "easeInOut", duration: 0.5 }}
    >
      {/* Alert Banner */}
      <AnimatePresence mode="wait">
        {shouldShowAlert() && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden border-b border-border/40 bg-primary-300/40"
          >
            <div className="content py-2 md:py-2.5">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <div className="hidden md:block w-10 shrink-0" aria-hidden="true" />
                <div className="flex min-w-0 flex-1 items-center justify-center overflow-hidden">
                  {alertData?.link ? (
                    <>
                      <Link
                        href={alertData.link}
                        onClick={() => handleDismissAlert()}
                        className="min-w-0 flex-1 truncate text-left !text-xs text-white !underline font-semibold transition-colors hover:text-white hover:decoration-white/70 md:hidden"
                        data-tina-field={tinaFieldSafe(alertData, "message")}
                      >
                        {alertData.message}
                      </Link>
                      <div className="hidden min-w-0 max-w-full items-center justify-center gap-3 md:flex">
                        <p
                          className="min-w-0 truncate !text-[15px] !text-white"
                          data-tina-field={tinaFieldSafe(alertData, "message")}
                        >
                          {alertData.message}
                        </p>
                        {alertData?.label && (
                          <Button
                            href={alertData.link}
                            onClick={() => handleDismissAlert()}
                            variant="link"
                            size="sm"
                            className="!h-auto shrink-0 !px-0 !py-0 !opacity-100 text-sm text-primary-200 underline decoration-primary-200/40 underline-offset-4 transition-colors hover:text-primary-100 hover:decoration-primary-200/70"
                          >
                            {alertData.label}
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <p
                      className="min-w-0 max-w-full truncate text-left !text-xs text-muted-foreground-light md:!text-[15px] md:text-center"
                      data-tina-field={tinaFieldSafe(alertData, "message")}
                    >
                      {alertData.message}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleDismissAlert}
                  className="shrink-0 p-0.5 text-muted-foreground transition-colors hover:text-muted-foreground-light"
                  aria-label="Dismiss alert"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Navbar Content */}
      <div className="py-4">
        <div className="content flex items-center justify-between gap-10">
          <Link
            href="/"
            aria-label="Binarly home"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Logo className="h-10" aria-hidden />
          </Link>

          {/* Mobile menu toggle button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative group translate-x-3"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <div className="relative flex items-center justify-center w-10 h-10 overflow-hidden">
              <div className="flex flex-col justify-between w-5 h-4">
                {/* Hamburger lines */}
                <motion.div
                  animate={{
                    x: isMobileMenuOpen ? 0 : 0,
                    opacity: isMobileMenuOpen ? 0 : 1,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="bg-secondary-700 h-0.5 w-5 origin-left"
                />
                <motion.div
                  animate={{
                    x: isMobileMenuOpen ? 0 : 0,
                    opacity: isMobileMenuOpen ? 0 : 1,
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                    delay: 0.05,
                  }}
                  className="bg-secondary-700 h-0.5 w-5"
                />
                <motion.div
                  animate={{
                    x: isMobileMenuOpen ? 0 : 0,
                    opacity: isMobileMenuOpen ? 0 : 1,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="bg-secondary-700 h-0.5 w-5 origin-left"
                />
                {/* X icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    animate={{
                      x: isMobileMenuOpen ? 0 : 0,
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="relative w-5 h-5"
                  >
                    <motion.div
                      animate={{
                        rotate: isMobileMenuOpen ? 45 : 0,
                      }}
                      transition={{
                        duration: 0.3,
                        ease: "easeInOut",
                      }}
                      className="absolute top-1/2 left-0 bg-secondary-700 h-0.5 w-5 -translate-y-1/2"
                    />
                    <motion.div
                      animate={{
                        rotate: isMobileMenuOpen ? -45 : 0,
                      }}
                      transition={{
                        duration: 0.3,
                        ease: "easeInOut",
                      }}
                      className="absolute top-1/2 left-0 bg-secondary-700 h-0.5 w-5 -translate-y-1/2"
                    />
                  </motion.div>
                </div>
              </div>
            </div>
          </button>

          <div className="flex-1 flex items-center justify-between max-lg:hidden">
            <NavMenu navMenus={navMenus} />
            <div className="flex items-center gap-4">
              {navButtons?.map((button: any, buttonIndex: number) => (
                <Button
                  key={buttonIndex}
                  variant={
                    buttonIndex === navButtons?.length - 1
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className={cn(!button.url && "cursor-not-allowed opacity-70")}
                  href={button?.url}
                >
                  {button.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile menu overlay */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="fixed inset-0 top-[73px] bg-background lg:hidden h-[calc(100svh-73px)] overflow-hidden"
            >
              <div className="relative h-full w-full">
                {/* Main Menu Layer */}
                <motion.div
                  animate={{ x: activeSubmenu !== null ? "-100%" : "0%" }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="absolute inset-0 flex flex-col pb-8"
                >
                  {/* Main Nav Items */}
                  <motion.div
                    className="flex-1 flex flex-col gap-1 divide-y divide-border"
                    initial="hidden"
                    animate="visible"
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.05,
                          delayChildren: 0.1,
                        },
                      },
                    }}
                  >
                    {navMenus?.map((navMenu: any, navMenuIndex: number) => (
                      <motion.button
                        key={navMenuIndex}
                        variants={{
                          hidden: { opacity: 0, x: -20 },
                          visible: {
                            opacity: 1,
                            x: 0,
                            transition: {
                              duration: 0.3,
                              ease: "easeOut",
                            },
                          },
                        }}
                        onTouchStart={() =>
                          setTouchedItem(`main-${navMenuIndex}`)
                        }
                        onTouchEnd={() => setTouchedItem(null)}
                        onTouchCancel={() => setTouchedItem(null)}
                        onClick={() => setActiveSubmenu(navMenuIndex)}
                        className={cn(
                          "flex items-center justify-between py-4 px-5 text-left text-white text-lg font-medium hover:bg-gray-900 active:bg-background-light rounded-none transition-colors duration-100",
                          touchedItem === `main-${navMenuIndex}` &&
                          "bg-background-light text-primary-200"
                        )}
                      >
                        <span>{navMenu.label}</span>
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </motion.button>
                    ))}
                  </motion.div>

                  {/* Buttons at bottom */}
                  <motion.div
                    className="flex flex-col gap-3 pt-6 px-6 border-t border-gray-800"
                    initial="hidden"
                    animate="visible"
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.1,
                          delayChildren: 0.3,
                        },
                      },
                    }}
                  >
                    {navButtons?.map((button: any, buttonIndex: number) => (
                      <motion.div
                        key={buttonIndex}
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              duration: 0.3,
                              ease: "easeOut",
                            },
                          },
                        }}
                      >
                        <Button
                          variant={
                            buttonIndex === navButtons?.length - 1
                              ? "default"
                              : "outline"
                          }
                          className={cn(
                            "w-full",
                            !button.url && "cursor-not-allowed opacity-70"
                          )}
                          href={button?.url}
                          onClick={() => {
                            if (button.url) {
                              setIsMobileMenuOpen(false);
                            }
                          }}
                        >
                          {button.label}
                        </Button>
                      </motion.div>
                    ))}
                  </motion.div>
                </motion.div>

                {/* Submenu Layer */}
                <AnimatePresence>
                  {activeSubmenu !== null && (
                    <motion.div
                      initial={{ x: "100%" }}
                      animate={{ x: "0%" }}
                      exit={{ x: "100%" }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="absolute inset-0 bg-background flex flex-col"
                    >
                      {/* Back button */}
                      <motion.div
                        className="border-b border-gray-800"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                      >
                        <button
                          onClick={() => setActiveSubmenu(null)}
                          className="flex items-center gap-3 text-primary-200 text-lg font-medium hover:text-primary-200 transition-colors w-full px-2 py-4"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                          <span>Back</span>
                        </button>
                      </motion.div>

                      {/* Submenu content */}
                      <div className="flex-1 overflow-y-auto">
                        <MobileSubmenu
                          navMenu={navMenus?.[activeSubmenu]}
                          onItemClick={() => {
                            setActiveSubmenu(null);
                            setIsMobileMenuOpen(false);
                          }}
                          touchedItem={touchedItem}
                          setTouchedItem={setTouchedItem}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}

function MobileSubmenu({
  navMenu,
  onItemClick,
  touchedItem,
  setTouchedItem,
}: {
  navMenu: any;
  onItemClick: () => void;
  touchedItem: string | null;
  setTouchedItem: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  if (!navMenu) return null;

  const isSimpleList = navMenu.__typename?.includes("SimpleList");
  const isMultiMenu = navMenu.__typename?.includes("MultiMenu");

  return (
    <motion.div
      className="flex flex-col"
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.08,
            delayChildren: 0.15,
          },
        },
      }}
    >
      {/* Title and description */}
      {navMenu?.title && (
        <motion.div
          className={cn(
            "flex flex-col gap-2 px-6 py-5",
            isSimpleList ? "border-b border-border" : ""
          )}
          variants={{
            hidden: { opacity: 0, y: 0, x: 20 },
            visible: {
              opacity: 1,
              y: 0,
              x: 0,
              transition: { duration: 0.3, ease: "easeOut" },
            },
          }}
        >
          <Link
            href={navMenu?.url || "#"}
            onClick={onItemClick}
            onTouchStart={() => setTouchedItem("title")}
            onTouchEnd={() => setTouchedItem(null)}
            onTouchCancel={() => setTouchedItem(null)}
            className={cn(
              "text-white font-semibold text-xl hover:text-primary-200 transition-colors duration-100",
              touchedItem === "title" && "text-primary-200"
            )}
          >
            {navMenu?.title}
          </Link>
          {navMenu?.description && (
            <p className="text-gray-400 text-sm">{navMenu?.description}</p>
          )}
        </motion.div>
      )}

      {/* Simple List */}
      {isSimpleList && navMenu?.menu && (
        <motion.ul
          className="flex flex-col gap-2 divide-y divide-border"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.05,
              },
            },
          }}
        >
          {navMenu.menu.map((item: any, itemIndex: number) => (
            <motion.li
              key={itemIndex}
              variants={{
                hidden: { opacity: 0, x: 30 },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.3, ease: "easeOut" },
                },
              }}
            >
              <Link
                href={item?.url || "#"}
                target={isInternalLink(item?.url) ? "_self" : "_blank"}
                onClick={onItemClick}
                onTouchStart={() => setTouchedItem(`simple-${itemIndex}`)}
                onTouchEnd={() => setTouchedItem(null)}
                onTouchCancel={() => setTouchedItem(null)}
                className={cn(
                  "block py-4 px-6 text-gray-100 rounded-lg transition-colors duration-100 text-lg active:bg-background-light",
                  {
                    "!cursor-not-allowed opacity-50": !item.url,
                    "hover:text-primary-100 active:text-primary-200": item.url,
                  },
                  touchedItem === `simple-${itemIndex}` &&
                  "bg-background-light text-primary-200"
                )}
              >
                {item.label}
              </Link>
            </motion.li>
          ))}
        </motion.ul>
      )}

      {/* Multi Menu */}
      {isMultiMenu && navMenu?.subMenus && (
        <motion.div
          className="flex flex-col gap-6 px-6"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
        >
          {navMenu.subMenus.map((subMenu: any, subMenuIndex: number) => (
            <motion.div
              key={subMenuIndex}
              className="flex flex-col gap-3"
              variants={{
                hidden: { opacity: 0, y: 0, x: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  x: 0,
                  transition: { duration: 0.3, ease: "easeOut" },
                },
              }}
            >
              {subMenu?.title && (
                <h3 className="font-mono uppercase text-primary-200 text-sm">
                  {subMenu.title}
                </h3>
              )}
              <motion.ul
                className="flex flex-col gap-2"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.05,
                    },
                  },
                }}
              >
                {subMenu?.links?.map((link: any, linkIndex: number) => (
                  <motion.li
                    key={linkIndex}
                    variants={{
                      hidden: { opacity: 0, x: 20 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: { duration: 0.3, ease: "easeOut" },
                      },
                    }}
                  >
                    <Link
                      href={link?.url || "#"}
                      target={isInternalLink(link?.url) ? "_self" : "_blank"}
                      onClick={onItemClick}
                      onTouchStart={() =>
                        setTouchedItem(`multi-${subMenuIndex}-${linkIndex}`)
                      }
                      onTouchEnd={() => setTouchedItem(null)}
                      onTouchCancel={() => setTouchedItem(null)}
                      className={cn(
                        "block py-3 px-0 text-gray-100 rounded-lg transition-colors duration-100 active:bg-background-light",
                        {
                          "!cursor-not-allowed opacity-50": !link.url,
                          "hover:text-primary-100 active:text-primary-200":
                            link.url,
                        },
                        touchedItem === `multi-${subMenuIndex}-${linkIndex}` &&
                        "bg-background-light text-primary-200"
                      )}
                    >
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Footer Menu */}
      {navMenu?.footerMenu &&
        navMenu?.footerMenu?.length > 0 &&
        navMenu.footerMenu[0] && (
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.3, ease: "easeOut" },
              },
            }}
          >
            <Link
              href={navMenu?.footerMenu?.[0]?.url || "#"}
              onClick={onItemClick}
              onTouchStart={() => setTouchedItem("footer")}
              onTouchEnd={() => setTouchedItem(null)}
              onTouchCancel={() => setTouchedItem(null)}
              className={cn(
                "bg-gray-900 px-6 py-4 rounded-lg hover:bg-gray-800 active:bg-background-light transition-colors duration-100 block",
                touchedItem === "footer" && "bg-background-light"
              )}
            >
              <div className="flex flex-col gap-2">
                {navMenu?.footerMenu?.[0]?.title && (
                  <div
                    className={cn(
                      "text-white font-medium transition-colors duration-100",
                      touchedItem === "footer" && "text-primary-200"
                    )}
                  >
                    {navMenu?.footerMenu?.[0]?.title}
                  </div>
                )}
                {navMenu?.footerMenu?.[0]?.description && (
                  <div
                    className={cn(
                      "text-gray-400 text-sm transition-colors duration-100",
                      touchedItem === "footer" && "text-primary-100"
                    )}
                  >
                    {navMenu?.footerMenu?.[0]?.description}
                  </div>
                )}
              </div>
            </Link>
          </motion.div>
        )}
    </motion.div>
  );
}

function NavMenu({ navMenus }: { navMenus: any }) {
  return (
    <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {navMenus?.map((navMenu: any, navMenuIndex: number) => {
          const isSimpleList = navMenu.__typename?.includes("SimpleList");
          const isMultiMenu = navMenu.__typename?.includes("MultiMenu");

          return (
            <NavigationMenuItem key={navMenuIndex}>
              <NavigationMenuTrigger
                data-tina-field={tinaFieldSafe(navMenu, "label")}
              >
                {navMenu.label}
              </NavigationMenuTrigger>
              <NavigationMenuContent className="p-0 z-50">
                {/* Simple List Template */}
                {isSimpleList && (
                  <div className="w-[220px]">
                    {/* Optional Title Section */}
                    {navMenu?.title && (
                      <div className="flex flex-col gap-2 group/title pt-6 pb-2 px-6">
                        <NavigationMenuPrimitive.Link asChild>
                          <Link
                            href={navMenu?.url || "#"}
                            className="text-white font-medium subpixel-antialiased text-[16px] flex items-center gap-2 group-hover/title:text-primary-200 cursor-pointer transition-all duration-300"
                          >
                            {navMenu?.title}
                          </Link>
                        </NavigationMenuPrimitive.Link>
                      </div>
                    )}

                    {/* Menu Items */}
                    <ul
                      className={cn("px-6", navMenu?.title ? "pb-4" : "py-4")}
                    >
                      {navMenu?.menu?.map((item: any, itemIndex: number) => (
                        <li key={itemIndex} className="py-2.5 text-gray-100">
                          <NavigationMenuPrimitive.Link asChild>
                            <Link
                              href={item?.url || "#"}
                              target={
                                isInternalLink(item?.url) ? "_self" : "_blank"
                              }
                              className={cn("whitespace-nowrap", {
                                "!cursor-not-allowed opacity-50": !item.url,
                                "hover:text-primary-100": item.url,
                              })}
                            >
                              {item.label}
                            </Link>
                          </NavigationMenuPrimitive.Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Multi Menu Template */}
                {isMultiMenu && (
                  <div
                    className={cn({
                      "w-[440px]": navMenu?.size === "medium",
                      "w-[600px]": navMenu?.size === "large",
                    })}
                  >
                    {/* Main Title/Description Section */}
                    {(navMenu?.title || navMenu?.description) && (
                      <div className="flex flex-col gap-2 group/title pt-6 pb-3 px-6">
                        {navMenu?.title && (
                          <NavigationMenuPrimitive.Link asChild>
                            <Link
                              href={navMenu?.url || "#"}
                              className="text-white font-medium subpixel-antialiased text-[16px] flex items-center gap-2 group-hover/title:text-primary-200 cursor-pointer transition-all duration-300"
                            >
                              {navMenu?.title}
                            </Link>
                          </NavigationMenuPrimitive.Link>
                        )}
                        {navMenu?.description && (
                          <div className="text-muted-foreground text-[14px]">
                            {navMenu?.description}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sub Menus Section */}
                    {navMenu?.subMenus && navMenu?.subMenus?.length > 0 && (
                      <div className={cn("grid gap-2 px-6 pb-4 grid-cols-2")}>
                        {navMenu?.subMenus?.map(
                          (subMenu: any, subMenuIndex: number) => (
                            <div
                              key={subMenuIndex}
                              className={cn(
                                "flex flex-col gap-2",
                                navMenu?.subMenus?.length > 2 &&
                                navMenu?.subMenus?.length % 2 !== 0 &&
                                subMenuIndex ===
                                navMenu?.subMenus?.length - 1 &&
                                "col-span-2"
                              )}
                            >
                              {subMenu?.title && (
                                <div className="font-mono uppercase text-primary-200 text-[15px] pt-2">
                                  {subMenu?.title}
                                </div>
                              )}
                              <ul
                                className={cn(
                                  "flex flex-col gap-1",
                                  // If more than 2 submenus and odd number and last one, grid grid-cols-2
                                  navMenu?.subMenus?.length > 2 &&
                                  navMenu?.subMenus?.length % 2 !== 0 &&
                                  subMenuIndex ===
                                  navMenu?.subMenus?.length - 1 &&
                                  "grid grid-cols-2"
                                )}
                              >
                                {subMenu?.links?.map(
                                  (link: any, linkIndex: number) => (
                                    <li
                                      key={linkIndex}
                                      className="py-2 text-gray-100"
                                    >
                                      <NavigationMenuPrimitive.Link asChild>
                                        <Link
                                          href={link?.url || "#"}
                                          target={
                                            isInternalLink(link?.url)
                                              ? "_self"
                                              : "_blank"
                                          }
                                          className={cn("whitespace-nowrap", {
                                            "!cursor-not-allowed opacity-50":
                                              !link.url,
                                            "hover:text-primary-100": link.url,
                                          })}
                                        >
                                          {link.label}
                                        </Link>
                                      </NavigationMenuPrimitive.Link>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {/* Footer Menu Section */}
                    {navMenu?.footerMenu &&
                      navMenu?.footerMenu?.length > 0 &&
                      navMenu.footerMenu[0] && (
                        <NavigationMenuPrimitive.Link asChild>
                          <Link
                            href={navMenu?.footerMenu?.[0]?.url || "#"}
                            className="bg-gray-1000 px-6 py-4 block group/footer cursor-pointer"
                          >
                            <div className="flex flex-col gap-2">
                              {navMenu?.footerMenu?.[0]?.title && (
                                <div className="text-white font-medium subpixel-antialiased text-[16px] group-hover/footer:text-primary-200 transition-all duration-300">
                                  {navMenu?.footerMenu?.[0]?.title}
                                </div>
                              )}
                              {navMenu?.footerMenu?.[0]?.description && (
                                <div className="text-muted-foreground text-[14px]">
                                  {navMenu?.footerMenu?.[0]?.description}
                                </div>
                              )}
                            </div>
                          </Link>
                        </NavigationMenuPrimitive.Link>
                      )}
                  </div>
                )}
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
}
